import DownArrow from 'icons/down.svg';
import UpArrow from 'icons/up.svg';

import { ENDPOINT, EXT_ENDPOINT } from 'consts/api';
import {
	BASE_FILE_TYPE_IMG_URL,
	DEFAULT_EDIT_RESPONSE,
	KEY_CODES,
	OTHER_FILE_TYPE,
	TIMESTAMP_EXCLUDED_MSG_TYPES,
} from 'consts/consts';
import {
	BOT_MODE,
	CARD_ACTION_TYPE,
	DATA_ICON_STATUS,
	MESSAGE_SENDER,
	MESSAGE_TYPE,
	NODE_TYPES,
	POPUP_OPEN_TYPE,
} from 'consts/enum';
import { LS } from 'consts/localStorage';
import { emitMessage } from 'helpers/socket';
import { ENGT_CHAT } from 'js/bot.model';
import { appendChatContent, appendStatusDivider, appendTimeStamp, appendToChat } from 'js/conversation';

import { api, downloadAPI } from './api';
import { emitAskUserIdEvent, emitInitMsgEvent, updateAttribute } from './socket';

const { AGENT, BOT } = MESSAGE_SENDER;

export const writeToLs = (a, b) => localStorage.setItem(a, b);

export const readFromLs = (a) => localStorage.getItem(a);

export const deleteFromLs = (a) => localStorage.removeItem(a);

export const initXMLhttp = () => {
	let xmlhttp;

	if (window.XMLHttpRequest) {
		// IE7,firefox chrome and others
		xmlhttp = new XMLHttpRequest();
	} else {
		// Internet Explorer
		xmlhttp = new ActiveXObject('Microsoft.XMLHTTP');
	}

	return xmlhttp;
};

export const isValidResponse = (http) => {
	if (http.readyState == 4 && http.status == 200) {
		const response = JSON.parse(http.responseText);
		if (response) {
			return response;
		}
	}

	return false;
};

export const getFontSetting = () => {
	if (readFromLs(LS().FONT_SETTING)) {
		return readFromLs(LS().FONT_SETTING);
	}
	if (window.innerWidth < 460) {
		return 'sm';
	}
	return 'md';
};

export const getExtension = (filename) => {
	const parts = filename.split('.');
	return parts[parts.length - 1];
};

export const getFileTypeImg = (fileType) => {
	const fileTypes = ['txt', 'doc', 'docx', 'ppt', 'pptx', 'csv', 'zip', 'pdf', 'xls'];

	if (fileTypes.includes(fileType)) {
		return `${BASE_FILE_TYPE_IMG_URL}/engt_${fileType}.svg`;
	}

	return OTHER_FILE_TYPE;
};

export const isValidResponseData = (http) => {
	const resp = isValidResponse(http);

	return resp && resp?.data ? resp.data : false;
};

export const getWidgetUrlParams = (a, c) => {
	const b = document.getElementsByTagName('script');
	const pathToCheck = isLocalHost() ? '/' : 'static/js/';
	for (let i = 0; i < b.length; i++) {
		if (b[i].src.indexOf(pathToCheck + a) > -1 || b[i].src.indexOf(pathToCheck + c) > -1) {
			const c = b[i].src.split('?').pop().split('&');
			const p = {};
			for (let j = 0; j < c.length; j++) {
				const d = c[j].split('=');
				p[d[0]] = d[1];
			}
			if (p?.['config']) {
				return p;
			}
		}
	}
	return {};
};

const getServerBaseUrl = (e) => {
	if (e === 'd' || e === 'dev') {
		return 'https://dev.engati.com';
	}
	if (e === 'p') {
		return 'https://app.engati.com';
	}
	if (e === 'l') {
		return 'http://localhost:5010';
	}
	if (e === 'qa') {
		return 'https://qa.engati.com';
	}
	if (e === 'staging') {
		return 'https://staging.engati.com';
	}
	if (e === 'op') {
		return 0;
	}
};

export const getParamsFromUrl = (name) =>
	decodeURIComponent(
		(new RegExp(`[?|&]${name}=` + '([^&;]+?)(&|#|;|$)').exec(location.search) || [null, ''])[1].replace(
			/\+/g,
			'%20'
		)
	) || null;

export const getStandaloneUrlParams = () => {
	let server = `${window.location.protocol}//${window.location.host}`;
	if (isLocalHost()) {
		server = getServerBaseUrl(getParamsFromUrl('env'));
	}

	return {
		config: {
			bot_key: getParamsFromUrl('bot_key'),
			launch_flow: getParamsFromUrl('launch_flow') || undefined,
			e: getParamsFromUrl('env'),
			botNameImage: getParamsFromUrl('bot_name_image'),
			data_attrs: getParamsFromUrl('data_attrs'),
			show_title: getParamsFromUrl('show_title'),
			debug: getParamsFromUrl('debug'),
			vid_sec: getParamsFromUrl('vid_sec'),
			set_default_brand: getParamsFromUrl('set_default_brand'),
			branding_key: getParamsFromUrl('branding_key') || 'default',
			loadChatHistory: !!getParamsFromUrl('loadChatHistory'),
			server: getParamsFromUrl('server') || server,
			bot_mode: BOT_MODE.STANDALONE,
			isChatOpened: true,
		},
	};
};

export const merge = (obj1, obj2) => {
	for (const p in obj2) {
		try {
			if (obj2[p].constructor == Object) {
				obj1[p] = merge(obj1[p], obj2[p]);
			} else {
				obj1[p] = obj2[p];
			}
		} catch (e) {
			obj1[p] = obj2[p];
		}
	}
	return obj1;
};

export const debug = (log) => {
	if (ENGT_CHAT.get('config.is_debug')) {
		console.log(log);
	}
};

export const getSaturation = (hex) => {
	if (!hex) return;
	hex = hex.replace('#', '');
	const rgb = [
		parseInt(hex.substring(0, 2), 16),
		parseInt(hex.substring(2, 4), 16),
		parseInt(hex.substring(4, 6), 16),
	];

	for (let i = 0; i < rgb.length; i++) {
		rgb[i] = rgb[i] / 255.0;
		if (rgb[i] <= 0.03928) {
			rgb[i] = rgb[i] / 12.92;
		} else {
			rgb[i] = Math.pow((rgb[i] + 0.055) / 1.055, 2.4);
		}
	}
	if (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2] > 0.179) {
		return '#403F42';
	}

	return '#ffffff';
};

export const getContrastingColor = (color1, color2) => {
	function componentToHex(c) {
		try {
			const hex = c.toString(16);
			return hex.length == 1 ? `0${hex}` : hex;
		} catch (e) {
			console.error('Error when converting component to hex', e);
		}
	}

	function rgbToHex([r, g, b]) {
		try {
			return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
		} catch (e) {
			console.error('Error when converting rgb to hex', e);
		}
	}

	function convert(color) {
		try {
			return /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i
				.exec(color)
				.map((x) => parseInt(`0x${x}`))
				.slice(1, 4);
		} catch (e) {
			console.error('Error when breaking down colors in hex', e);
		}
	}

	if (!color1) {
		return '#000000';
	}
	if (!color2) {
		color2 = color1;
	}

	try {
		const [c1, c2] = [color1, color2].map((x) => convert(x));
		const mixedColors = [];
		c1.forEach((c, i) => mixedColors.push(parseInt((c1[i] + c2[i]) / 2)));
		return getSaturation(rgbToHex(mixedColors));
	} catch (e) {
		console.error('Error when generating contrasting color ', e);
		return '#000000';
	}
};

export const hasClass = (el, val) => {
	const pattern = new RegExp(`(^|\\s)${val}(\\s|$)`);

	if (!el?.className) {
		return false;
	}

	return pattern.test(el.className);
};

export const addClass = (ele, cls) => {
	if (!hasClass(ele, cls) && ele) ele.className += ` ${cls}`;
};

export const removeClass = (ele, cls) => {
	if (hasClass(ele, cls) && ele) {
		const reg = new RegExp(`(\\s|^)${cls}(\\s|$)`);

		ele.className = ele.className.replace(reg, ' ');
	}
};

export const isEllipsisActive = (e) => e && e.offsetHeight + 2 < e.scrollHeight;

export const disableInputArea = () => {
	const textArea = document.getElementById('engtUserMessage');
	if (textArea) {
		textArea.setAttribute('disabled', true);
	}
};

export const enableInputArea = () => {
	const textArea = document.getElementById('engtUserMessage');
	if (textArea) {
		textArea.setAttribute('disabled', false);
	}
};

export const focusInputArea = () => {
	const textArea = document.getElementById('engtUserMessage');
	textArea && textArea.focus();
};

export const getPreComputedBounds = (element, targetNode) => {
	const el = element;
	el.style.visibility = 'hidden';
	el.style.position = 'absolute';
	targetNode.style.display = 'block';
	targetNode.appendChild(el);
	const result = {
		height: el.height,
		width: el.width,
		offsetHeight: el.offsetHeight,
		offsetWidth: el.offsetWidth,
		getBoundingClientRect: el.getBoundingClientRect(),
	};
	el.parentNode.removeChild(el);
	targetNode.display = 'none';
	return result;
};

export const generateUUID = () => {
	let d = new Date().getTime();
	const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
		const r = (d + Math.random() * 16) % 16 | 0;
		d = Math.floor(d / 16);
		return (c == 'x' ? r : (r & 0x3) | 0x8).toString(16);
	});
	return uuid;
};

export const generate6DigitUUID = () => {
	let firstPart = (Math.random() * 46656) | 0;
	let secondPart = (Math.random() * 46656) | 0;
	firstPart = `000${firstPart.toString(36)}`.slice(-3);
	secondPart = `000${secondPart.toString(36)}`.slice(-3);
	return firstPart + secondPart;
};

export const getUrlVars = () => {
	const vars = {};
	const parts = window.location.href.replace(/[?&]+([^=&]+)=([^&]*)/gi, (m, key, value) => {
		vars[key] = value;
	});
	return vars;
};

export const animation = {
	messagesIn: () => {
		const chatContent = document.getElementById('engtChatContent')?.children;

		for (let i = 0; i < chatContent?.length || 0; i++) {
			const sender =
				[AGENT, BOT].indexOf(chatContent[i]?.getAttribute?.('data-msg-sender')) > -1 ? 'bot' : 'user';
			const delay = i % 2 === 0 ? '0.3s' : '0.2s';
			chatContent[
				i
			].style.animation = `message-slide-in-${sender} 0.5s cubic-bezier(.65,.56,.14,1.01) ${delay} both`;
		}
	},
	messagesOut: () => {
		const chatContent = document.getElementById('engtChatContent')?.children;

		for (let i = 0; i < chatContent?.length || 0; i++) {
			chatContent[i].style.animation = '';
		}
	},
	launchScreenIn: () => {
		const launchScreenNodes = document.querySelector('.engt-launcher-details')?.children;
		const askQuery = document.querySelector('.engt-launch-query-input');

		for (let i = 0; i < launchScreenNodes?.length || 0; i++) {
			launchScreenNodes[i].style.animation = `launch-screen-animation-in 0.4s ease-out ${0.2 * (i + 1)}s both`;
			askQuery.style.animation = 'growXFromHalf 0.3s ease-out 0.4s both';
		}
	},
	launchScreenOut: () => {
		const launchScreenNodes = document.querySelector('.engt-launcher-details')?.children;
		const askQuery = document.querySelector('.engt-launch-query-input');

		for (let i = 0; i < launchScreenNodes?.length || 0; i++) {
			launchScreenNodes[i].style.animation = null;
			askQuery.style.animation = null;
		}
	},
};

export const focusElement = (element) => {
	if (typeof element === 'string') {
		element = document.querySelector(element);
	}

	if (element) {
		element.setAttribute('tabindex', -1);
		element.setAttribute('tabindex', 0);
		element.focus?.();
	}
};

export const getUserWebAttributeObject = (webUrl, webPage) => {
	const urlVars = getUrlVars();
	const user_web_attr = {};
	if (webUrl) {
		user_web_attr['url'] = webUrl;
	}
	if (webPage) {
		user_web_attr['page'] = webPage;
	}
	const utm_parameters = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
	for (let i = 0; i < utm_parameters.length; i++) {
		const utm_parameter_value = urlVars[utm_parameters[i]];
		if (utm_parameter_value) {
			user_web_attr[utm_parameters[i]] = utm_parameter_value;
		}
	}
	const payload = {};
	payload['name'] = 'user_web';
	payload['value'] = JSON.stringify(user_web_attr);
	return payload;
};

export const isMobileOrTablet = function () {
	let check = false;
	(function (a) {
		if (
			/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(
				a
			) ||
			/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
				a.substr(0, 4)
			)
		) {
			check = true;
		}
	})(navigator.userAgent || navigator.vendor || window.opera);
	return check;
};

export const onEscPress = (cb) => (e) => {
	const keyCode = e.which ? e.which : e.keyCode;
	if (keyCode === 27 && !e.shiftKey) {
		e.preventDefault();
		cb(e, this);
	}
};

export const callbackOnKeyDown = (cb = () => e.target.click(), code = KEY_CODES.ENTER, e = event) => {
	const keyCode = e.which ? e.which : e.keyCode;
	if (keyCode === code && !e.shiftKey) {
		e.preventDefault();
		cb();
	}
};

export const changeElementStyleAttr = (selector, attr, value) => {
	if (document.querySelector(selector)) {
		document.querySelector(selector).style[`${attr}`] = value;
	}
};

export const getTextFromHTML = (html) => {
	const div = document.createElement('div');
	div.innerHTML = html || '';

	return div.innerText;
};

export const setCustomTagHTML = (parent, child) => {
	if (!parent) return;

	try {
		if (typeof child === 'string') parent.innerHTML = child;
		else parent.appendChild(child);

		removeParent(parent);
	} catch (e) {
		console.error('error in append child', e);
	}
};

export const removeParent = (toRemove) => {
	const fragment = document.createDocumentFragment();

	while (toRemove.firstChild) {
		fragment.appendChild(toRemove.firstChild);
	}
	toRemove.parentNode.replaceChild(fragment, toRemove);
};

export const removeElement = (element) => {
	if (!element) return;

	if (element && element.parentNode) {
		element.parentNode.removeChild(element);
	}
};

export const onEditResponse = (flowKey, nodeKey) => {
	const data = {
		message: {
			flow_node_key: `${flowKey}__${nodeKey}`,
			type: 'trigger_flow_node',
		},
	};
	const defaultMsg = ENGT_CHAT.get('config.default_edit_response') || DEFAULT_EDIT_RESPONSE;
	ENGT_CHAT.get('sendMessage')(defaultMsg, data);
};

const getTriggerElement = (
	fontColor
) => `<div id="engt-triggers-wrapper" class="engt-triggers-wrapper" style="display: none">
		<div id="engt-triggers-cross-div" class="engt-triggers-cross-div">
		<svg
		width="11"
		height="11"
		viewBox="-0.5 -0.5 9 9"
		fill="${fontColor}"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path
			fill-rule="evenodd"
			clip-rule="evenodd"
			d="M0.939771 0.104663L0.0558872 0.988546L3.14943 4.08209L0.0559435 7.17557L0.939827 8.05946L4.03331 4.96597L7.12696 8.05961L8.01084 7.17573L4.9172 4.08209L8.0109 0.988389L7.12701 0.104505L4.03331 3.1982L0.939771 0.104663Z"
		/>
	</svg>
		</div>
		<div id="engt-triggers" class="engt-triggers">
			<div id="engt-triggers-message" class="engt-triggers-message"></div>
			<div id="engt-triggers-separator" class="engt-triggers-separator"></div>
			<div id="engt-triggers-button-group" class="engt-triggers-button-group">
			</div>
		</div>
	</div>`;

export const showCallout = (calloutMessage, triggerInfo, options, attributes, calloutsColorStyle) => {
	const triggersTagElement = document.querySelector('engagement-triggers');
	const root = document.documentElement;
	const style = document.createElement('style');
	style.type = 'text/css';
	if (ENGT_CHAT.get('config.isChatOpened')) {
	} else {
		setCustomTagHTML(triggersTagElement, getTriggerElement(calloutsColorStyle?.fontColor));

		const triggerWrapper = document.getElementById('engt-triggers-wrapper');
		const triggerMessage = document.getElementById('engt-triggers-message');
		const triggerButtonGroup = document.getElementById('engt-triggers-button-group');
		const triggerCrossDiv = document.getElementById('engt-triggers-cross-div');

		const enable_trigger_analytics = ENGT_CHAT.get('config.enable_trigger_analytics_config');

		const ga_enabled_in_shopify = 'ga' in window;

		if (ga_enabled_in_shopify && enable_trigger_analytics) {
			window.ga('send', 'event', 'Engagement', 'Popup Visible', triggerInfo.name);
		}
		const isPreviousTriggerOpened = triggerMessage.innerHTML;
		triggerMessage.innerHTML = calloutMessage;
		triggerButtonGroup.innerHTML = '';
		options?.forEach((option) => {
			const optionButton = document.createElement('button');
			optionButton.className = 'engt-triggers-button';
			optionButton.innerHTML = option.text;
			optionButton.addEventListener('click', () => {
				if (ga_enabled_in_shopify && enable_trigger_analytics) {
					window.ga('send', 'event', 'Conversion', 'Click', triggerInfo.name);
				}

				const botMessages = [{ type: 'TEXT', text: calloutMessage }];

				let attributes_url_encoded = '';
				attributes.forEach((attribute) => {
					attributes_url_encoded = `${
						attributes_url_encoded + (attributes_url_encoded && '&') + attribute.name
					}=${attribute.value}`;
				});

				const postback = `flow_${option.flow_key}||data_${attributes_url_encoded}`;

				const tempConfig = {
					isFlowTriggeredFromTriggers: true,
					flowKey: option.flow_key,
					attributes,
					text: option.text,
					botMessages,
					postback,
					triggerStartNewConversation: false,
				};

				if (ENGT_CHAT.get('triggerSessionExpired')) {
					tempConfig.triggerStartNewConversation = true;
				}

				ENGT_CHAT.set('triggerButtonConfig', tempConfig);
				ENGT_CHAT.get('onLauncherClick')();

				ENGT_CHAT.set('triggerButtonConfig', {
					isFlowTriggeredFromTriggers: false,
				});
				ENGT_CHAT.set('triggerSessionExpired', false);
			});
			triggerButtonGroup.appendChild(optionButton);
		});
		triggerWrapper.style.display = 'block';

		if (ENGT_CHAT.get('config.autoPopupType') === POPUP_OPEN_TYPE.CLOSED && !isPreviousTriggerOpened) {
			const closedCalloutElement = document.getElementById('engtClosedCallout');
			if (closedCalloutElement && !hasClass(closedCalloutElement, 'engt-closed-callout-hidden')) {
				triggerWrapper.style.bottom = '84px';
			}
		}

		root.style.setProperty('--engt-triggers-background-color', calloutsColorStyle?.backgroundColor);
		root.style.setProperty('--engt-triggers-message-color', calloutsColorStyle?.fontColor);
		root.style.setProperty('--engt-triggers-cross-background-color', calloutsColorStyle?.backgroundColor);
		root.style.setProperty('--engt-triggers-button-text-color', calloutsColorStyle.buttonTextColor);
		root.style.setProperty('--engt-triggers-button-background-color', calloutsColorStyle.buttonColor);
		root.style.setProperty('--engt-triggers-separator-color', hexToRgb(calloutsColorStyle.fontColor, 0.2));

		triggerCrossDiv.addEventListener('click', () => {
			triggerWrapper.style.display = 'none';
		});
	}
};

export const preventScaleOrZoom = () => {
	const meta = document.createElement('meta');
	meta.id = 'engtMetaTag';
	meta.name = 'viewport';
	meta.content =
		'user-scalable=no, initial-scale=1, maximum-scale=1, minimum-scale=1, width=device-width, height=device-height, target-densitydpi=device-dpi';
	document.getElementsByTagName('head')[0].appendChild(meta);
	document.documentElement.style.setProperty('touch-action', 'pan-x pan-y');
	document.documentElement.style.setProperty('height', '100%');
};

export const removeScaleZoomRestriction = () => {
	const meta = document.querySelector('#engtMetaTag');
	meta?.parentNode?.removeChild?.(meta);
	document.documentElement.style.removeProperty('touch-action');
	document.documentElement.style.removeProperty('height');
};

export const generateBtnOnClickFnc = (button, tabAction) => {
	const { dropdowns, index, msgId, type = '', title, value, payload, url, otherChannelsData } = button || {};
	let onClick = () => {};

	switch (type.toUpperCase()) {
		case CARD_ACTION_TYPE.FAQ:
			onClick = function () {
				ENGT_CHAT.get('sendQuery')(value || payload, {
					tab_msg_action: tabAction,
					skipAppendToDom: ENGT_CHAT.get('config.isCtaResponseHidden'),
				});
			};
			break;
		case CARD_ACTION_TYPE.URL:
		case CARD_ACTION_TYPE.WEB_URL:
		case CARD_ACTION_TYPE.PHONE_NUMBER:
			onClick = function () {
				window.open(value || url || payload, '_blank');
			};
			break;
		case CARD_ACTION_TYPE.PATH:
		case CARD_ACTION_TYPE.POSTBACK:
			onClick = function () {
				let dataAttributes = '';
				if (dropdowns?.data?.length) {
					dataAttributes += payload.indexOf('||data_') > -1 ? '' : '||data_';
					dropdowns.data.map((dropdown, idx) => {
						if (idx > 0 || payload.indexOf('||data_') > -1) {
							dataAttributes += '&';
						}
						dataAttributes += `${dropdowns.payload}.${idx}=${ENGT_CHAT.get(
							`carouselDropdowns.${msgId}-${idx}`
						)}`;
					});
				}
				if (otherChannelsData) {
					const { platform, bot, user } = otherChannelsData;
					const payloadData = {
						request: JSON.stringify({
							postback: value || payload + dataAttributes,
							type: 'postback',
							text: title,
							userId: user,
						}),
						userId: user,
					};
					api('POST', `${window.location.origin}/${platform}/submitPostback/${bot}`, {
						payload: JSON.stringify(payloadData),
					})
						.then((resp) => {
							const response = isValidResponse(resp);
							if (response) {
								if (response.status) {
									const pageContainer = document.getElementById('engt-popup');
									pageContainer.innerHTML =
										'<div>Thanks for the input. Please close this web view to return to the chat.</div>';
									pageContainer.classList.add('engt-redirect-open');
									window.top.close();
								}
							}
						})
						.catch((error) => {
							debug(error);
						});
				} else {
					ENGT_CHAT.get('sendQuery')(title, {
						message: { postback: value || payload + dataAttributes, type: 'postback', text: title },
						tab_msg_action: tabAction,
						skipAppendToDom: ENGT_CHAT.get('config.isCtaResponseHidden'),
					});
				}
			};
			break;
	}

	return onClick;
};

export const copyToClipboardEvent = (text) => {
	const data = [new window.ClipboardItem({ 'text/plain': new Blob([text], { type: 'text/plain' }) })];

	navigator.clipboard.write(data).then(
		() => {
			actionCallback(true);
		},
		() => {
			actionCallback(false);
		}
	);
};

export const applyCode = (code) => {
	try {
		fetch(`${window.location.origin}/cart.js`, {
			method: 'get',
		})
			.then((response) => response.json())
			.then(
				(cartDetails) =>
					`${
						EXT_ENDPOINT().CART +
						cartDetails['items'].map((item) => `${item.id}:${item.quantity}`).join(',')
					}?discount=${code}`
			)
			.then((applyCodeUrl) => fetch(applyCodeUrl, { method: 'get' }))
			.then((response) => {
				actionCallback(true);
			})
			.catch((err) => {
				actionCallback(false);
			});
	} catch (e) {
		console.error(e);
	}
};

const actionCallback = function (success, response) {
	const msgBody = {
		bot_key: ENGT_CHAT.get('config.bot_key'),
		user_id: ENGT_CHAT.get('status.sessionKey'),
		message: {
			type: 'request_callback',
			success,
			response,
		},
		key: ENGT_CHAT.get('config.key'),
		is_debug: ENGT_CHAT.get('config.is_debug'),
	};
	emitMessage('user_msg', msgBody);
};

export const getTime = (date) => date.toLocaleTimeString('en-US', { hour12: true, hour: 'numeric', minute: '2-digit' });

export const getDate = (date) => date.toLocaleDateString('en-GB', { month: 'long', day: '2-digit' });

export const formatBotTimeStamp = (inputDate = new Date()) => {
	const date = getDate(inputDate);
	const year = inputDate.getFullYear();
	const time = getTime(inputDate);
	const finalDate = `${date}, ${year} ${time}`;
	return finalDate;
};

export const checkIfAnchorTag = (msg) => {
	const linkRegex = new RegExp('<a[^>]*>(.*?)</a>');
	return linkRegex.test(msg);
};

export const inverseObj = (obj) => {
	const retobj = {};
	for (const key in obj) {
		retobj[obj[key]] = key;
	}

	return retobj;
};

export const linkify = (inputText) => {
	let notContainsHttp;
	// URLs starting with http(s)://, http(s):// + some string., or www. and not present inside <a> tags
	const replacePattern1 =
		/(?!([^<]+)<\/a>)((?:https?:\/\/[A-Z]*\.)|(www\.)|(?:https?:\/\/))[-A-Z0-9+&@#\/%?=~_|!:,;]+(\.[-A-Z0-9+&@#?\/%=~_|]+)+(?=<|\s|$)/gim;
	// URLs starting with "www." (without // before it, or it'd re-link the ones done above).
	// (?![^<]*>)(?<!\/)(www\.[\S]+(\b|$)) for look behind and can be used in future.
	const replacePattern2 = /((^|[^\/])(www\.[\S]+(\b|$)))/gim;
	// Change email addresses to mailto:: links.
	const replacePattern3 = /(?!([^<]+)<\/a>)([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6})/gim;
	// URLs in href without http
	const replacePattern4 = /((^|[^\/])href='(www\.[\S]+(\b|$)))/gim;
	// anchor tags created from replacePattern3 can also have formatting tags enclosing them
	const replacePattern5 = /(<[a-z]>)(<a href="mailto:(.*?)">(.*?)<\/a>)(<\/[a-z]>)/gim;

	let replacedText = inputText;
	replacedText = replacedText.replace(replacePattern4, " href='http://$3");
	let linksWithoutTags = replacedText.match(replacePattern1);

	if (linksWithoutTags) {
		// removing duplicate links
		linksWithoutTags = linksWithoutTags.filter((link, index, links) => links.indexOf(link) == index);
		if (linksWithoutTags) {
			linksWithoutTags.forEach((link) => {
				notContainsHttp = link.match(replacePattern2);
				link = link.replace(/\//g, '\\/');
				link = link.replace(/\./g, '\\.');
				link = link.replace(/\?/g, '\\?');
				link = link.replace(/\=/g, '\\=');
				const regex = new RegExp(`(?!([^<]+)>)(^|[ ]|-|\\()(${link})`, 'gim');
				if (notContainsHttp) {
					replacedText = replacedText.replace(regex, '$2<a href="http://$3" target="_blank">$3</a>');
				} else {
					replacedText = replacedText.replace(regex, '$2<a href="$3" target="_blank">$3</a>');
				}
			});
		}
	}
	// replaces mail ids in anchor tag and add mailto: before mail ids so that it will open default mail app
	if (replacedText.indexOf('mailto:') === -1) {
		replacedText = replacedText.replace(replacePattern3, '<a href="mailto:$2">$2</a>');
		replacedText = replacedText.replace(replacePattern5, '<a href="mailto:$3">$1$3$5</a>');
	}
	return replacedText;
};

export const copyToClipboard = (contentToBeCopied) => {
	navigator.clipboard.writeText(contentToBeCopied);
};

export const scrollTo = (element, to, duration) => {
	const start = element.scrollTop;
	const change = to - start;
	let currentTime = 0;
	const increment = 20;

	const animateScroll = function () {
		currentTime += increment;
		const val = Math.easeInOutQuad(currentTime, start, change, duration);
		element.scrollTop = val;
		if (currentTime < duration) {
			setTimeout(animateScroll, increment);
		}
	};
	animateScroll();
};

Math.easeInOutQuad = function (t, b, c, d) {
	t /= d / 2;
	if (t < 1) return (c / 2) * t * t + b;
	t--;
	return (-c / 2) * (t * (t - 2) - 1) + b;
};

export const getFormatedFileSize = (size) => {
	const convertedSize = (size / (1024 * 1024)).toFixed(2);
	if (parseInt(convertedSize) === 0) {
		return `${(size / 1024).toFixed(2)}KB`;
	}
	if (Number.isInteger(convertedSize)) {
		return `${convertedSize}MB`;
	}
	return '';
};

export const resetNotificationCount = () => {
	document.addEventListener('visibilitychange', (event) => {
		if (document.hidden) {
			ENGT_CHAT.set('infocus', false);
		} else {
			ENGT_CHAT.set('infocus', true);
			const title = document.title.split(')').pop();
			ENGT_CHAT.set('notificationCount', 1);
			document.title = title;
		}
	});

	let isNotified = JSON.parse(readFromLs(LS().IS_NOTIFED));
	ENGT_CHAT.set('config.isNotified', isNotified);
	if (isNotified) {
		isNotified = false;
		writeToLs(LS().IS_NOTIFED, JSON.stringify(isNotified));
	}
};

export const scrollHighlightToView = () => {
	const frame = document.getElementById('engtChatContent');
	frame.scrollBy({
		top: frame.scrollHeight,
		behavior: 'smooth',
	});
};

export const updateLocalStorageMsgs = (identifier, identifierVal, newValue = {}) => {
	const prevMessages = readFromLs(LS().PREV_MESSAGES);
	if (prevMessages) {
		const messagesList = JSON.parse(prevMessages);
		for (let i = 0; i < messagesList.length; i++) {
			if (messagesList[i][`${identifier}`] === identifierVal) {
				messagesList[i] = {
					...messagesList[i],
					...newValue,
				};
				break;
			}
		}
		writeToLs(LS().PREV_MESSAGES, JSON.stringify(messagesList));
	}
};

export const storeSkipInLS = (data) => {
	const msgData = {
		agent_avatar: undefined,
		agent_name: undefined,
		message: 'skip',
		message_id: null,
		messagetype: MESSAGE_TYPE.SKIP,
		sender: 'user',
		timestamp: new Date().getTime(),
		user_id: ENGT_CHAT.get('status.sessionKey'),
		bot_ref: ENGT_CHAT.get('config.botRef'),
		bot_key: ENGT_CHAT.get('config.bot_key'),
	};
	appendChatContent(msgData);
};

export const debounce = (func, wait) => {
	let timeout;

	return function executedFunction(...args) {
		const later = () => {
			clearTimeout(timeout);
			func(...args);
		};

		clearTimeout(timeout);
		timeout = setTimeout(later, wait);
	};
};

export const hexToRgb = (hex, opacity) => {
	const hexToRgba = hex
		.replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i, (m, r, g, b) => `#${r}${r}${g}${g}${b}${b}`)
		.substring(1)
		.match(/.{2}/g)
		.map((x) => parseInt(x, 16));
	return `rgba(${hexToRgba}, ${opacity})`;
};

export const getEllipsisedText = (text, size) =>
	text && text.length > size ? `${text.substring(0, size)} . . . ` : text;

export const downloadFile = (url, type) => {
	downloadAPI('GET', `${ENDPOINT().DOWNLOAD_URL}?url=${url}`, {
		async: true,
		responseType: 'blob',
	})
		.then((resp) => {
			const element = document.createElement('a');
			element.setAttribute('href', URL.createObjectURL(resp.response));
			element.setAttribute('download', `media.${type}` || '');
			element.style.display = 'none';
			document.body.appendChild(element);
			element.click();
			document.body.removeChild(element);
		})
		.catch((error) => console.log('error', error));
};

export const isLocalHost = () => window.location.href.indexOf('http://localhost:8080') > -1;

export const badgeNumberFormatter = (value) =>
	Math.abs(value) > 999 ? `${(Math.abs(value) / 1000).toFixed(1)}k` : Math.abs(value);

export const downloadIndieFlowerFont = () => {
	const style = document.createElement('style');
	style.innerHTML = `
	@import url('https://fonts.googleapis.com/css2?family=Spline+Sans:wght@300;400;500;600;700&display=swap');
	@font-face {
		font-family: 'Indie Flower script=all rev=2';
		font-style: normal;
		font-weight: 400;
		src: url(https://fonts.gstatic.com/l/font?kit=m8JVjfNVeKWVnh3QMuKkFcZla0GA1dOueNYET-Fmdpk-d21Aj7xnXaUjjY72D9DSIjGkU6jFp9SFiH_A_FoehJyTrQ2KzW1Rfb0c1Alj8NwWzipxiySCTbvzmJiEBuR3KG_xwB13pzmf2p7Pid2Yp5OEaOzZYZ5OFcTizpCfcjsp1NFVSJvdLaH9dvFnKdA&skey=cdda66716f19c532&v=v11)
			format('woff2');
	}`;
	document.head.appendChild(style);
};
export const downloadRobotoFont = () => {
	const style = document.createElement('style');
	style.innerHTML = `
	@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700&display=swap');
	@font-face {
		font-family: 'Roboto';
		font-style: normal;
		font-weight: 400;
		src: url(https://fonts.gstatic.com/l/font?kit=KFOMCnqEu92Fr1ME7kSn66aGLdTylUAMQXC89YmC2DPNWubEbWmWggvWlkwn7X8WhBoUiaKmIpiDLU9JQ7_9&skey=a0a0114a1dcab3ac&v=v47) format('woff2');
	}`;
	document.head.appendChild(style);
};

/*
 * adding custom event listener for changes in localstorage
 */
export const engtLSEventListener = (localStorageKey, eventName, eventListenerCallback) => {
	if (document.body.getAttribute('data-isEngtListenerAdded') !== eventName) {
		const originalSetItem = localStorage.setItem;

		localStorage.setItem = function (key, value) {
			const event = new Event(eventName);

			event.value = value;
			event.key = key;

			if (key === localStorageKey && value === 'true') {
				document.dispatchEvent(event);
			}

			originalSetItem.apply(this, arguments);
		};

		eventName && document.addEventListener(eventName, eventListenerCallback, false);
		document.body.setAttribute('data-isEngtListenerAdded', eventName);
	}
};

export const stopCurrentFlowandSendMsg = (msg, config, flowKey) => {
	const messages = JSON.parse(readFromLs(LS().PREV_MESSAGES)) || [];

	if (messages.length) {
		const contentContainer = document.getElementById('engtChatContent');
		contentContainer.innerHTML = '';
		writeToLs(LS().PREV_MESSAGES, JSON.stringify([]));
		ENGT_CHAT?.get('sendQuery')('Skip', {
			message: {
				postback: 'SESSION_TIMEOUT_STOP_CURRENT_FLOW',
				type: 'postback',
				text: 'Skip',
				skipStoringMsg: true,
				tab_msg_action: 'skip',
			},
			skipAppendToDom: true,
		}) || function () {};

		setTimeout(() => {
			if (msg) {
				ENGT_CHAT?.get('sendQuery')(msg, config) || function () {};
			} else if (flowKey) {
				emitAskUserIdEvent();
				emitInitMsgEvent(flowKey);
			}
		}, 500);
	} else if (msg) {
		ENGT_CHAT?.get('sendQuery')(msg, config) || function () {};
	} else if (flowKey) {
		emitAskUserIdEvent();
		emitInitMsgEvent(flowKey);
	}
};

const checkStartConvo = (msg) => {
	if (ENGT_CHAT.get('startConvoAdded')) {
		ENGT_CHAT.get('startNewConversation')(msg);
	} else {
		stopCurrentFlowandSendMsg(msg);
	}
};

const listenerCb = (message, flowKey) => {
	setTimeout(() => stopCurrentFlowandSendMsg(message, {}, flowKey), 300);
};

export const startFreshFlow = (flowKey) => {
	if (ENGT_CHAT.get('startConvoAdded')) {
		removeStartConvo();
		const contentContainer = document.getElementById('engtChatContent');
		contentContainer.innerHTML = '';
		writeToLs(LS().PREV_MESSAGES, JSON.stringify([]));
		emitAskUserIdEvent();
		emitInitMsgEvent(flowKey);
	} else {
		stopCurrentFlowandSendMsg('', {}, flowKey);
	}
};
export const createEngtChatHooks = () => {
	window.EngtChat = {
		...(window.EngtChat || {}),
		open: window.ENGT_CHAT?.onLauncherClick || function () {},
		close: window.ENGT_CHAT?.onMinimize || function () {},
		startFlow(flowKey) {
			if (readFromLs(LS().GOT_UID) === 'true') {
				if (ENGT_CHAT.get('config.isChatOpened')) {
					startFreshFlow(flowKey);
				} else {
					ENGT_CHAT?.get('onLauncherClick')();
					startFreshFlow(flowKey);
				}
			} else {
				engtLSEventListener(LS().GOT_UID, 'itemInserted', listenerCb.call(this, '', flowKey));
				if (!ENGT_CHAT.get('config.isChatOpened')) {
					ENGT_CHAT?.get('onLauncherClick')();
				}
				if (ENGT_CHAT.get('startConvoAdded')) {
					removeStartConvo();
					emitAskUserIdEvent();
					emitInitMsgEvent(flowKey);
				}
			}
		},
		sendQuery(msg) {
			if (readFromLs(LS().GOT_UID) === 'true') {
				if (ENGT_CHAT.get('config.isChatOpened')) {
					checkStartConvo(msg);
				} else {
					ENGT_CHAT?.get('onLauncherClick')();
					checkStartConvo(msg);
				}
			} else {
				engtLSEventListener(LS().GOT_UID, 'itemInserted', listenerCb.call(this, msg));
				if (!ENGT_CHAT.get('config.isChatOpened')) {
					const triggerWelcomeMsg = false;
					ENGT_CHAT?.get('onLauncherClick')(triggerWelcomeMsg);
				}
				if (ENGT_CHAT.get('startConvoAdded')) {
					ENGT_CHAT.get('startNewConversation')(msg);
				}
			}
		},
		updateAttribute,
	};
};

export const removeStartConvo = () => {
	// remove close chat divider
	const closeChatDivider = Array.from(document.querySelectorAll('.engt-status-divider-wrapper'))?.pop?.();
	const contentContainer = document.getElementById('engtChatContent');
	const askQueryField = document.getElementById('engtQueryInput');
	const onModalClose = ENGT_CHAT.get('onModalClose');

	closeChatDivider?.remove?.();
	onModalClose && onModalClose('', true);
	ENGT_CHAT.set('startConvoAdded', false);
	ENGT_CHAT.get('renderReplyInput')();
	// find last msg after removing status dividers, if any
	const lastMessage = Array.from(contentContainer.children) || [];

	if (askQueryField) {
		askQueryField.removeAttribute('disabled');
	}
	// add timestamp separator after starting a new convo
	if (!TIMESTAMP_EXCLUDED_MSG_TYPES.includes(lastMessage[lastMessage.length - 1]?.getAttribute('data-messagetype'))) {
		appendTimeStamp({});
	}
};

const updateCTAButtons = (node, confirmId, skipId, editId, skipBasedEdit = false) => {
	const confirmBtn = node.querySelector(`#${confirmId}`);
	const skipBtn = node.querySelector(`#${skipId}`);
	removeElement(confirmBtn);
	removeElement(skipBtn);
	if (ENGT_CHAT.get('config.edit_user_input') && !skipBasedEdit) {
		const editBtn = node.querySelector(`#${editId}`);
		editBtn.style.display = 'block';
	}
};

export const syncMessagesAcrossTabs = (event) => {
	if (event.key === LS().TAB_MESSAGES && readFromLs(LS().TAB_MESSAGES)) {
		try {
			const data = JSON.parse(readFromLs(LS().TAB_MESSAGES));
			const { action, skipAppendToDom, statusData = {}, selectedValue, tabId } = data;
			const currentSocketId = ENGT_CHAT.get('socketId') || '';
			if (tabId !== currentSocketId) {
				switch (action) {
					case 'add_user_msg': {
						!skipAppendToDom && appendToChat(data, { storeMsgInLs: false });
						break;
					}
					case 'disable_session_timeout': {
						removeStartConvo();
						break;
					}
					case 'enable_session_timeout': {
						appendStatusDivider(statusData);
						ENGT_CHAT.get('renderStartConversation')();
						break;
					}
					case 'update_smo_msg': {
						const { id, selectedValue } = data;
						const node = document.getElementById(id);
						node.querySelectorAll(`button:not(#editBtn-${id})`).forEach((input, index) => {
							input.disabled = true;
							if (index === selectedValue) {
								input.classList.add('engt-button-base-hover');
							}
						});
						if (ENGT_CHAT.get('config.edit_user_input')) {
							const editBtn = node.querySelector(`#editBtn-${id}`);
							editBtn.style.display = 'block';
						}
						break;
					}
					case 'update_file_msg': {
						const { fileName, fileLink, fileType, fileSize, id, selectedValue } = data;
						const fileNode = document.querySelector(`#engt-file-${id}`);
						if (selectedValue != 'skip') {
							const file = {
								name: fileName,
								src: fileLink,
								type: fileType,
								size: fileSize,
							};
							const overlay = fileNode.querySelector('.preview-overlay');
							ENGT_CHAT.get('showUploadedFile')(file, true);
							fileNode
								.querySelector('.engt-msg-file-upload-preview')
								.classList.add('engt-msg-file-upload-complete');
							fileNode.querySelector('[data-id="inputField"]').disabled = true;
							removeElement(overlay);
						}
						updateCTAButtons(
							fileNode,
							'fileUploadBtn',
							'fileSkipBtn',
							`editBtn-${id}`,
							selectedValue === 'skip'
						);
						break;
					}
					case 'update_rud_msg': {
						const { rudId, selectedValue } = data;
						const rudNode = document.getElementById(rudId);
						const err = rudNode.querySelector(`#rud-error-${rudId}`);
						const inputField = rudNode.querySelector(`#rud-input-${rudId}`);
						const datepicker = document.getElementById(`engtDatePicker-${rudId}`);
						const successMsg = rudNode.querySelector('validated-msg');

						inputField?.remove();
						if (datepicker) {
							datepicker.remove();
						}
						const msg = document.createElement('strong');
						msg.style.width = '100%';
						msg.innerHTML = selectedValue;

						if (err) {
							err.classList.add('engt-hidden');
						}
						updateCTAButtons(rudNode, `confirmBtn-${rudId}`, 'rud-skipBtn', `editBtn-${rudId}`);
						setCustomTagHTML(successMsg, msg);

						break;
					}
					case 'update_form_msg': {
						const { id, toggleBtnId, selectedValue } = data;
						const formParent = document.getElementById(id);
						if (selectedValue === 'skip') {
							formParent.parentNode.removeChild(formParent);
							return;
						}
						formParent.innerHTML = selectedValue;
						const toggleBtn = document.getElementById(toggleBtnId);
						if (toggleBtn) {
							formParent.classList.add('engt-msg-formnode-shrink');
							toggleBtn.addEventListener('click', function () {
								if (this.getAttribute('data-open') === 'false') {
									formParent.classList.remove('engt-msg-formnode-shrink');
									this.setAttribute('data-open', true);
									this.querySelector('span').innerText = 'Show less';
									this.querySelector('.engt-show-more-icon').src = UpArrow;
								} else {
									formParent.classList.add('engt-msg-formnode-shrink');
									this.setAttribute('data-open', false);
									this.querySelector('span').innerText = 'Show more';
									this.querySelector('.engt-show-more-icon').src = DownArrow;
								}
							});
						}
						break;
					}
					case 'update_slider_msg': {
						const { id, disableLabels, percentage } = data;
						const slider = document.querySelector(`#engt-slider-input-${id}`);
						const parentNode = document.querySelector(`#engtSliderMsg-${id}`);
						if (parentNode) {
							slider.disabled = true;
							if (selectedValue != 'skip') {
								slider.value = selectedValue;
								slider.style.background = `linear-gradient(90deg, ${ENGT_CHAT.get(
									'config.buttonSelectorColor'
								)} ${percentage}%, #fff 0)`;
							}
							const confirm = parentNode.querySelector('#confirmBtn');
							const skip = parentNode.querySelector('#skipBtn');
							const labels = parentNode.querySelector(`#engtSliderLabels-${id}`);
							const labelContainer = parentNode.querySelector(`#engt-slider-val-${id}`);
							if (disableLabels && labels) {
								labels.style.pointerEvents = 'none';
							}
							if (labelContainer) {
								labelContainer.innerText = `${parseInt(selectedValue)}%`;
							}
							skip && parentNode.removeChild(skip);
							parentNode.removeChild(confirm);
						}
						break;
					}
					case 'update_feedback_msg': {
						const { ratingId } = data;
						const { IS_DISABLED, SELECTED_INDEX, IS_SELECTED } = DATA_ICON_STATUS;
						const feedbackRate = document.getElementById(`stars-${ratingId}`);
						const { parentNode } = feedbackRate;
						if (parentNode) {
							const feedbackInp = parentNode.querySelector('#engFeedbackInput');
							feedbackRate.setAttribute(IS_DISABLED, true);
							if (feedbackInp) {
								feedbackInp.disabled = true;
							}

							updateCTAButtons(
								parentNode,
								'confirmBtn',
								'skipBtn',
								`editBtn-${ratingId}`,
								selectedValue === 'skip'
							);
							if (selectedValue != 'skip') {
								feedbackRate.setAttribute(SELECTED_INDEX, selectedValue);
								const ratings = parentNode.querySelectorAll('.engt-rating');
								ratings.forEach((star, index) => {
									if (index <= selectedValue) {
										star.setAttribute(IS_SELECTED, true);
									} else {
										star.setAttribute(IS_SELECTED, false);
									}
								});
							}
						}

						break;
					}
				}
				ENGT_CHAT.get('enableAllInput')();
				deleteFromLs(LS().TAB_MESSAGES);
			}
		} catch (e) {
			LOGGER('errored out while syncing messages across tabs: ', e);
		}
	}
};
export const LOGGER = (msg, ...args) => {
	if (ENGT_CHAT.get('config.is_debug')) {
		console.log(msg, ...args);
	}
};
export const isDisableInputRequired = (data) => {
	const { source_node_type, message } = data;
	if (source_node_type === NODE_TYPES.RCV_INP && !data.validated) {
		return true;
	}
	if (message.type === MESSAGE_TYPE.IDENTITY && !data?.message?.data?.hasOwnProperty('login_success')) {
		return true;
	}
	return false;
};

export const focusContenteditableDiv = () => {
	const inputArea = document.getElementById('engtUserMessage');
	if (inputArea) {
		inputArea.focus();
		const range = document.createRange();
		const sel = window.getSelection?.();
		range.setStart(
			inputArea.childNodes?.[inputArea.childNodes.length - 1],
			inputArea.childNodes?.[inputArea.childNodes.length - 1].length
		);
		range.collapse(true);
		if (sel) {
			sel.removeAllRanges();
			sel.addRange(range);
		}
	}
};

export const updateBotLanguageToLS = (code) => {
	if (!code) {
		return;
	}
	writeToLs(LS().USER_LOCALE, code);
	window.onENGTLanguageChange?.(code);
};

export const updateBotLanguage = (langCode) => {
	const prevLangCode = document.getElementById('botLang').innerHTML.toLowerCase();
	if (prevLangCode !== langCode && document.getElementById(langCode)) {
		document.getElementById('botLang').innerHTML = langCode.toUpperCase();
		const prevLang = document.getElementById(readFromLs(LS().USER_LOCALE));
		const tickElement = document.getElementById('tickForLang');
		prevLang?.removeChild(tickElement);
		prevLang?.classList.remove('selected-option');

		const currentLang = document.getElementById(langCode);
		const existingChild = document.getElementById(`option-${langCode}`);
		currentLang?.classList.add('selected-option');
		currentLang?.insertBefore(tickElement, existingChild);
	}
};
